import React, { useState } from 'react';
import { Alert, Box, Button, Container, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { adminApi, getStoredAdminToken, setStoredAdminToken } from '../../lib/api';
import AdminVideos from './AdminVideos';
import AdminArticles from './AdminArticles';
import AdminSubmissions from './AdminSubmissions';

// Minimal, shared-secret-token content management console. Not indexed,
// not linked from navigation, and gated server-side on every request —
// see api/_lib/content.js#requireAdmin. The token is kept in
// sessionStorage only (cleared when the tab closes), never localStorage.
export default function Admin() {
  const [token, setToken] = useState(getStoredAdminToken());
  const [draft, setDraft] = useState('');
  const [checking, setChecking] = useState(false);
  const [authError, setAuthError] = useState('');
  const [tab, setTab] = useState(0);

  const unlock = async () => {
    setChecking(true);
    setAuthError('');
    try {
      await adminApi.listVideos(draft);
      setStoredAdminToken(draft);
      setToken(draft);
    } catch (err) {
      setAuthError(err.status === 503 ? 'Admin access is not configured on this deployment (ADMIN_TOKEN is unset).' : 'Incorrect token.');
    } finally {
      setChecking(false);
    }
  };

  const lock = () => {
    setStoredAdminToken('');
    setToken('');
    setDraft('');
  };

  if (!token) {
    return (
      <Container maxWidth="xs" sx={{ py: 10 }}>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Paper variant="outlined" sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Admin access
          </Typography>
          {authError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {authError}
            </Alert>
          ) : null}
          <TextField
            label="Admin token"
            type="password"
            fullWidth
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && unlock()}
            sx={{ mb: 2 }}
            autoFocus
          />
          <Button variant="contained" fullWidth onClick={unlock} disabled={checking || !draft}>
            {checking ? 'Checking…' : 'Unlock'}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800}>
          Content admin
        </Typography>
        <Button variant="outlined" onClick={lock}>
          Lock
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Videos" />
        <Tab label="Blog articles" />
        <Tab label="Enquiries" />
      </Tabs>

      <Box role="tabpanel" hidden={tab !== 0}>
        {tab === 0 ? <AdminVideos token={token} /> : null}
      </Box>
      <Box role="tabpanel" hidden={tab !== 1}>
        {tab === 1 ? <AdminArticles token={token} /> : null}
      </Box>
      <Box role="tabpanel" hidden={tab !== 2}>
        {tab === 2 ? <AdminSubmissions token={token} /> : null}
      </Box>
    </Container>
  );
}
